import { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

interface DepositCardCaptureProps {
  captured: boolean;
  onCaptured: (paymentMethodId: string) => void;
}

/**
 * Inline form that captures a credit-only PaymentMethod the customer can
 * back a security-deposit hold with. Validates `card.funding === "credit"`
 * client-side before reporting the PaymentMethod id up; the backend
 * (`StripeHandler.validate_card_type/2`) re-validates server-side.
 *
 * Copy of the quote-review component (intentionally duplicated to keep
 * quote-review behavior identical while embed checkout adopts the flow).
 */
export function DepositCardCapture({
  captured,
  onCaptured,
}: DepositCardCaptureProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCapture = async () => {
    if (!stripe || !elements) return;

    setIsCapturing(true);
    setError(null);

    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(submitResult.error.message || "Please complete the card.");
        setIsCapturing(false);
        return;
      }

      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({ elements });

      if (pmError || !paymentMethod) {
        setError(pmError?.message || "Could not validate the card.");
        setIsCapturing(false);
        return;
      }

      const funding = paymentMethod.card?.funding;
      if (funding !== "credit") {
        setError(
          "This card must be a credit card. Please use a different card.",
        );
        setIsCapturing(false);
        return;
      }

      onCaptured(paymentMethod.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred.");
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-200 rounded p-3">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {captured ? (
        <p className="text-xs text-green-700 font-medium">
          Deposit card saved. You can complete the reservation now.
        </p>
      ) : (
        <button
          type="button"
          onClick={handleCapture}
          disabled={!stripe || !elements || isCapturing}
          className="w-full inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCapturing ? "Validating…" : "Save deposit card"}
        </button>
      )}
    </div>
  );
}
